import {
  Component,
  DestroyRef,
  effect,
  inject,
  Input,
  untracked,
} from '@angular/core';
import {
  ColDef,
  ColGroupDef,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { DialogService } from '@sui/dialog';

import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { injectToggleUserStatusMutation } from 'src/app/api/queries/company.queries';
import {
  UserWriteState,
  injectUserWriteStates,
} from 'src/app/api/queries/user.queries';
import { AgBooleanCellRendererComponent } from '../../../ag-grid/components/cell-renderers/ag-boolean-cell-renderer/ag-boolean-cell-renderer.component';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { AgObjectListCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-object-list-cell-renderer/ag-object-list-cell-renderer.component';
import {
  AgToggleCellRendererComponent,
  AgDateRangeFilterComponent,
  booleanSetFilterDef,
  DataGridComponent,
  GridToolbarActionDirective,
} from '@sensika/ag-grid-kit';
import { DataGridHostBase } from 'src/app/ag-grid/data-grid-host-base';
import { buildNomenclatureColumnDef } from 'src/app/ag-grid/utils/nomenclature-column.utils';
import { buildAuditTrailColumnGroup } from 'src/app/ag-grid/utils/audit-trail-columns.utils';
import { ImportUsersDialogComponent } from 'src/app/admin/dialogs/import-users-dialog/import-users-dialog.component';
import { ObjectCreatorService } from 'src/app/core/services/object-creator.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { ToastService } from '@services/toast.service';
import { User } from 'src/app/core/models/core.models';
import { ClientAdminApiService } from 'src/app/api/services/client-admin-api.service';
import { OBJECT_SETTINGS_ACCESS_LEVEL } from 'src/app/core/object-settings-access-level.constants';
import { NomObjectClass } from 'src/app/core/constants';
import { SuiButtonComponent } from '@sui/button';
import { SuiTooltipDirective } from '@sui/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  imports: [
    DataGridComponent,
    SuiButtonComponent,
    SuiTooltipDirective,
    GridToolbarActionDirective,
    TranslatePipe,
  ],
})
export class UserListComponent extends DataGridHostBase {
  /** Set when embedded as a company-editor dialog tab; scopes the list to that company. */
  @Input() companyId: string;

  private readonly clientAdminApi = inject(ClientAdminApiService);
  private readonly objectCreatorService = inject(ObjectCreatorService);
  private readonly nomenclatureService = inject(NomenclatureService);
  private readonly dialogService = inject(DialogService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly toggleUserStatusMutation = injectToggleUserStatusMutation();

  readonly viewId = ViewId.Users;
  readonly title = this.translateService.instant('user-list.title.user-list');
  readonly icon: IconProp = ['fas', 'user'];

  // ─── Write mirroring ───────────────────────────────────────────────────
  // Rows live in ag-grid's SSRM block cache, so user writes made anywhere (the
  // routed editor, the editor dialog, a card menu's re-invite) are mirrored onto
  // them from the mutation cache rather than invalidated.
  private readonly userWrites = injectUserWriteStates();
  /** Writes submitted before this grid existed are history, not events. */
  private readonly mountedAt = Date.now();
  private readonly mirroredWriteIds = new Set<number>();

  constructor() {
    super();
    effect(() => {
      const writes = this.userWrites();
      untracked(() => this.mirrorUserWrites(writes));
    });
  }

  readonly fetchRows = (request: IServerSideGetRowsRequest) =>
    this.clientAdminApi.getUsersAgGridModel(request, this.companyId);

  /** Export menu -> server-side CSV of the whole filtered resultset. */
  readonly exportCsvServerSide = (): void => {
    const request = this.grid?.lastRequest;
    if (!request) {
      return;
    }
    this.clientAdminApi
      .exportUsersCsv(request, this.companyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () =>
          this.toastService.showError(
            this.translateService.instant('user-list.toast.export-failed'),
          ),
      });
  };

  readonly columnDefs: Array<ColDef | ColGroupDef> = this.buildColumnDefs();

  /**
   * Pending -> row spinner (idempotent, re-run on every state change); the first
   * settled state -> row patch on success, spinner off on error. `mutationId`
   * de-duplicates because settled entries stay in the cache for its gcTime.
   */
  private mirrorUserWrites(writes: UserWriteState[]): void {
    for (const write of writes) {
      if (
        write.submittedAt < this.mountedAt ||
        this.mirroredWriteIds.has(write.mutationId)
      ) {
        continue;
      }

      if (write.status === 'pending') {
        if (write.userId) {
          this.setUserRowLoading(write.userId, true);
        }
        continue;
      }

      this.mirroredWriteIds.add(write.mutationId);

      if (write.status === 'success' && write.user) {
        this.updateUserRow(write.user);
      } else if (write.userId) {
        this.setUserRowLoading(write.userId, false);
      }
    }
  }

  private updateUserRow(updatedUser: User) {
    const api = this.grid?.api;
    if (!api) return;

    const rowNode = api.getRowNode(updatedUser.id);

    if (rowNode) {
      // replace the entire row object
      rowNode.setData(updatedUser);

      // optional: refresh the whole row (usually not needed, but safe)
      api.refreshCells({
        rowNodes: [rowNode],
        force: true,
      });
    } else {
      // fallback: row not currently loaded in cache / viewport
      api.refreshServerSide({
        route: [],
        purge: false,
      });
    }
  }

  private setUserRowLoading(userId: string, loading: boolean) {
    const api = this.grid?.api;
    if (!api) return;

    const rowNode = api.getRowNode(userId);
    if (rowNode) {
      rowNode.setDataValue('isLoading', loading);
      // optional, usually not required:
      api.refreshCells({ rowNodes: [rowNode], force: true });
    }
  }

  private buildColumnDefs(): Array<ColDef | ColGroupDef> {
    return [
      {
        headerName: this.translateService.instant('user-list.column.user-name'),
        field: 'display_name',
        filter: 'agTextColumnFilter',
        minWidth: 180,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'user',
          shouldInvite: true,
          clickAccessLevel: OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.USER],
          onClick: (user: any, row: any) =>
            this.objectEditor.editUser(user?.id ?? row?.id),
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant(
          'user-list.column.company-name',
        ),
        field: 'default_company.name',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'default_company',
          icon: ['fas', 'building'],
          clickAccessLevel:
            OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.COMPANY],
          onClick: (company: any) => this.objectEditor.editCompany(company?.id),
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('user-list.column.email'),
        field: 'email',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: this.translateService.instant(
          'user-list.column.email-alerts',
        ),
        field: 'email_alerts',
        filter: 'agTextColumnFilter',
        maxWidth: 240,
        hide: true,
      },
      {
        headerName: this.translateService.instant(
          'user-list.column.time-last-active',
        ),
        field: 'time_last_active.absolute',
        filter: AgDateRangeFilterComponent,
        cellRenderer: AgDateCellRendererComponent,
        maxWidth: 200,
      },
      {
        headerName: this.translateService.instant('user-list.column.is-active'),
        field: 'is_enabled',
        cellRenderer: AgToggleCellRendererComponent,
        cellRendererParams: {
          property: 'is_enabled',
          action: (rowData) => this.toggleUserStatus(rowData),
        },
        filter: 'agSetColumnFilter',
        maxWidth: 140,
        filterParams: {
          values: ['true', 'false'],
        },
        resizable: true,
      },
      {
        headerName: this.translateService.instant(
          'user-list.column.security-and-permissions',
        ),
        marryChildren: true,
        children: [
          {
            ...buildNomenclatureColumnDef(
              this.translateService,
              this.nomenclatureService,
              {
                headerNameKey: 'user-list.column.access-level',
                fieldKey: 'user_access_level.id',
                fieldValue: 'user_access_level.name',
                nomenclatureName: 'user_access_level',
              },
            ),
            maxWidth: 150,
          },
          {
            headerName: this.translateService.instant('user-list.column.roles'),
            field: 'roles',
            cellRenderer: AgObjectListCellRendererComponent,
            cellRendererParams: {
              objectType: 'roles',
              icon: ['fas', 'user-shield'],
            },
            filter: 'agTextColumnFilter',
            autoHeight: true,
            wrapText: true,
            width: 320,
            cellStyle: { lineHeight: 'normal' },
          },
          {
            headerName: this.translateService.instant(
              'user-list.column.groups',
            ),
            field: 'groups',
            cellRenderer: AgObjectListCellRendererComponent,
            cellRendererParams: {
              objectType: 'groups',
              icon: ['fas', 'users'],
            },
            filter: 'agTextColumnFilter',
            autoHeight: true,
            wrapText: true,
            cellStyle: { lineHeight: 'normal' },
          },
        ],
      },
      {
        headerName: this.translateService.instant(
          'user-list.column.user-management-details',
        ),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant(
              'user-list.column.invited',
            ),
            field: 'platform_invitation.is_invited',
            ...booleanSetFilterDef(this.translateService),
            cellRenderer: AgBooleanCellRendererComponent,
            cellRendererParams: {
              isActive: 'data.platform_invitation.is_invited',
            },
            autoHeight: true,
            wrapText: true,
            cellStyle: { lineHeight: 'normal' },
          },
          {
            headerName: this.translateService.instant(
              'user-list.column.invited-by',
            ),
            field: 'invited_by',
            filter: 'agTextColumnFilter',
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              object: 'invited_by',
              clickAccessLevel:
                OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.USER],
              onClick: (user) => this.objectEditor.editUser(user.id),
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            headerName: this.translateService.instant(
              'user-list.column.invited-on',
            ),
            field: 'platform_invitation.time_invited',
            filter: AgDateRangeFilterComponent,
            maxWidth: 200,
            cellRenderer: AgDateCellRendererComponent,
            cellRendererParams: {
              type: 'updated',
            },
          },
          {
            headerName: this.translateService.instant(
              'user-list.column.last-invited',
            ),
            field: 'platform_invitation.time_last_invited.absolute',
            colId: 'platform_invitation.time_last_invited',
            filter: AgDateRangeFilterComponent,
            maxWidth: 200,
            cellRenderer: AgDateCellRendererComponent,
          },
          {
            headerName: this.translateService.instant(
              'user-list.column.invitations',
            ),
            field: 'platform_invitation.invited_count',
            type: 'rightAligned',
            filter: 'agNumberColumnFilter',
            maxWidth: 130,
            hide: true,
          },
        ],
      },
      buildAuditTrailColumnGroup(this.translateService, {
        area: 'admin',
        defaultSort: { colId: 'time_created', direction: 'desc' },
        onUserClick: this.openUser,
      }),
    ];
  }

  createUser() {
    this.grid?.refreshWhenSaved(this.objectCreatorService.createUser());
  }

  importUsers() {
    this.dialogService.openTabsDialog(ImportUsersDialogComponent, {
      data: {},
      closeOnClick: false,
      closeOnEscape: true,
      width: '25%',
      height: '50%',
      minWidth: 400,
      minHeight: 800,
      maxWidth: 820,
      maxHeight: 1020,
    });
  }

  toggleUserStatus(rowData: any) {
    this.toggleUserStatusMutation.mutate(rowData.data.id);
  }
}
